import { addOns } from "./content";
import snapshot from "./generated/product-snapshot.json";
import type { AddOnDefinition, CartSong, ProductCatalog, ProductVariant, StorefrontProduct } from "./types";

const CONTRACT_VERSION = "tt-v1";
const SIGNATURE_LINE_TYPE = "signature";
const ADD_ON_LINE_TYPE = "add-on";

const storeDomain = normalizeDomain(import.meta.env.VITE_SHOPIFY_STORE_DOMAIN || "tune-tailor.myshopify.com");
const apiVersion = import.meta.env.VITE_SHOPIFY_API_VERSION || "2026-04";
const storefrontToken = import.meta.env.VITE_SHOPIFY_STOREFRONT_TOKEN || "";
const signatureHandle = import.meta.env.VITE_SHOPIFY_PRODUCT_HANDLE || "custom-song";
const onlineStoreOrigin = (import.meta.env.VITE_SHOPIFY_ONLINE_STORE_ORIGIN || "https://checkout.tunetailor.org").replace(/\/$/, "");

export type CheckoutRequest =
  | { type: "redirect"; url: string }
  | { type: "form"; action: string; clearCartUrl: string; fields: CheckoutFormField[] };

export interface CheckoutFormField {
  name: string;
  value: string;
}

const catalogQuery = `#graphql
  query TuneTailorCatalog(
    $signature: String!
    $instrumental: String!
    $lyricVideo: String!
    $commercialLicence: String!
  ) {
    signature: product(handle: $signature) {
      ...ProductFields
    }
    instrumental: product(handle: $instrumental) {
      ...ProductFields
    }
    lyricVideo: product(handle: $lyricVideo) {
      ...ProductFields
    }
    commercialLicence: product(handle: $commercialLicence) {
      ...ProductFields
    }
  }

  fragment ProductFields on Product {
    handle
    title
    description
    availableForSale
    variants(first: 10) {
      nodes {
        id
        title
        availableForSale
        price {
          amount
          currencyCode
        }
      }
    }
  }
`;

const cartCreateMutation = `#graphql
  mutation TuneTailorCartCreate($input: CartInput!) {
    cartCreate(input: $input) {
      cart {
        id
        checkoutUrl
      }
      userErrors {
        field
        message
      }
      warnings {
        code
        message
        target
      }
    }
  }
`;

export function initialCatalog(): ProductCatalog {
  return normalizeSnapshot(snapshot);
}

export async function loadCatalog(): Promise<ProductCatalog> {
  if (!storefrontToken) {
    return initialCatalog();
  }

  try {
    const data = await shopifyFetch<{
      signature: StorefrontApiProduct | null;
      instrumental: StorefrontApiProduct | null;
      lyricVideo: StorefrontApiProduct | null;
      commercialLicence: StorefrontApiProduct | null;
    }>(catalogQuery, {
      signature: signatureHandle,
      instrumental: addOns[0].handle,
      lyricVideo: addOns[1].handle,
      commercialLicence: addOns[2].handle,
    });

    const products = [
      data.signature,
      data.instrumental,
      data.lyricVideo,
      data.commercialLicence,
    ]
      .filter((product): product is StorefrontApiProduct => Boolean(product))
      .map(productFromStorefrontApi);

    if (!products.some((product) => product.handle === signatureHandle)) {
      return initialCatalog();
    }

    return { source: "storefront-api", products };
  } catch (error) {
    console.warn("Falling back to Shopify product snapshot.", error);
    return initialCatalog();
  }
}

export async function createCheckoutUrl(
  songs: CartSong[],
  catalog: ProductCatalog,
  locale = "en",
): Promise<string> {
  const request = await createCheckoutRequest(songs, catalog, locale);
  if (request.type !== "redirect") {
    throw new Error("Checkout requires form submission for this cart.");
  }
  return request.url;
}

export async function createCheckoutRequest(
  songs: CartSong[],
  catalog: ProductCatalog,
  locale = "en",
): Promise<CheckoutRequest> {
  if (songs.length === 0) {
    throw new Error("Add at least one song before checkout.");
  }

  if (storefrontToken) {
    return { type: "redirect", url: await createStorefrontCart(songs, catalog) };
  }

  return createFormCheckout(songs, catalog, locale);
}

export function getSignatureProduct(catalog: ProductCatalog): StorefrontProduct | undefined {
  return catalog.products.find((product) => product.handle === signatureHandle);
}

export function getProductByHandle(catalog: ProductCatalog, handle: string): StorefrontProduct | undefined {
  return catalog.products.find((product) => product.handle === handle);
}

export function selectedVariant(product: StorefrontProduct | undefined): ProductVariant | undefined {
  return product?.variants.find((variant) => variant.available) || product?.variants[0];
}

export function formatMoney(money: { amount: string | number; currencyCode: string }, locale = "en"): string {
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: money.currencyCode,
  }).format(Number(money.amount));
}

export function songLineAttributes(song: CartSong): Array<{ key: string; value: string }> {
  return [
    { key: "Song title", value: song.title },
    { key: "Genre", value: song.genre },
    { key: "Song details", value: "Attached privately" },
    { key: "_Story or lyrics", value: song.story },
    { key: "_Song ID", value: song.id },
    { key: "_TT Line Type", value: SIGNATURE_LINE_TYPE },
    { key: "_TT Contract Version", value: CONTRACT_VERSION },
  ];
}

export function addOnLineAttributes(
  song: CartSong,
  addOn: AddOnDefinition,
): Array<{ key: string; value: string }> {
  return [
    { key: "Add-on type", value: addOn.type },
    { key: "Assigned song", value: song.title },
    { key: "_Song ID", value: song.id },
    { key: "_TT Line Type", value: ADD_ON_LINE_TYPE },
    { key: "_TT Contract Version", value: CONTRACT_VERSION },
  ];
}

export function subtotalForSongs(songs: CartSong[], catalog: ProductCatalog): number {
  const signature = selectedVariant(getSignatureProduct(catalog));
  const signaturePrice = signature ? Number(signature.price.amount) : 0;

  return songs.reduce((total, song) => {
    const addOnTotal = song.addOns.reduce((sum, key) => {
      const addOn = addOns.find((candidate) => candidate.key === key);
      const variant = selectedVariant(addOn ? getProductByHandle(catalog, addOn.handle) : undefined);
      return sum + (variant ? Number(variant.price.amount) : 0);
    }, 0);
    return total + signaturePrice + addOnTotal;
  }, 0);
}

async function createStorefrontCart(songs: CartSong[], catalog: ProductCatalog): Promise<string> {
  const lines = cartLines(songs, catalog).map((line) => ({
    merchandiseId: line.variant.storefrontId,
    quantity: 1,
    attributes: line.attributes,
  }));

  const data = await shopifyFetch<{
    cartCreate: {
      cart: { checkoutUrl: string } | null;
      userErrors: Array<{ field: string[] | null; message: string }>;
      warnings: Array<{ code: string; message: string; target: string | null }>;
    };
  }>(cartCreateMutation, { input: { lines } });

  const userError = data.cartCreate.userErrors[0];
  if (userError) {
    throw new Error(userError.message);
  }

  const warning = data.cartCreate.warnings?.[0];
  if (warning) {
    throw new Error(warning.message);
  }

  if (!data.cartCreate.cart?.checkoutUrl) {
    throw new Error("Shopify did not return a checkout URL.");
  }

  return data.cartCreate.cart.checkoutUrl;
}

function createFormCheckout(songs: CartSong[], catalog: ProductCatalog, locale: string): CheckoutRequest {
  const fields: CheckoutFormField[] = [];

  cartLines(songs, catalog).forEach((line, index) => {
    fields.push(
      { name: `items[${index}][id]`, value: String(line.variant.id) },
      { name: `items[${index}][quantity]`, value: "1" },
    );

    for (const attribute of line.attributes) {
      fields.push({
        name: `items[${index}][properties][${attribute.key}]`,
        value: attribute.value,
      });
    }
  });

  fields.push({ name: "return_to", value: localizedCheckoutPath(locale) });

  return {
    type: "form",
    action: `${onlineStoreOrigin}/cart/add`,
    clearCartUrl: `${onlineStoreOrigin}/cart/clear.js`,
    fields,
  };
}

async function shopifyFetch<TData>(
  query: string,
  variables: Record<string, unknown>,
): Promise<TData> {
  const response = await fetch(`https://${storeDomain}/api/${apiVersion}/graphql.json`, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      "x-shopify-storefront-access-token": storefrontToken,
    },
    body: JSON.stringify({ query, variables }),
  });

  const body = (await response.json()) as {
    data?: TData;
    errors?: Array<{ message: string }>;
  };

  if (!response.ok || body.errors?.length || !body.data) {
    throw new Error(body.errors?.[0]?.message || `Shopify returned ${response.status}`);
  }

  return body.data;
}

function normalizeDomain(value: string): string {
  return value.replace(/^https?:\/\//, "").replace(/\/$/, "");
}

function normalizeSnapshot(raw: unknown): ProductCatalog {
  const parsed = raw as ProductCatalog;
  return {
    source: "snapshot",
    generatedAt: parsed.generatedAt,
    products: parsed.products || [],
  };
}

function productFromStorefrontApi(product: StorefrontApiProduct): StorefrontProduct {
  return {
    handle: product.handle,
    title: product.title,
    description: product.description,
    available: product.availableForSale,
    variants: product.variants.nodes.map((variant) => ({
      id: numericShopifyId(variant.id),
      storefrontId: variant.id,
      title: variant.title,
      available: variant.availableForSale,
      price: variant.price,
    })),
  };
}

function numericShopifyId(id: string): number {
  return Number(id.match(/(\d+)$/)?.[1] || 0);
}

function localizedCheckoutPath(locale: string): string {
  return locale && locale !== "en" ? `/${locale}/checkout` : "/checkout";
}

function cartLines(
  songs: CartSong[],
  catalog: ProductCatalog,
): Array<{ variant: ProductVariant; attributes: Array<{ key: string; value: string }> }> {
  const signatureVariant = selectedVariant(getSignatureProduct(catalog));
  if (!signatureVariant) {
    throw new Error("Signature Song is not available right now.");
  }

  return songs.flatMap((song) => {
    const signatureLine = {
      variant: signatureVariant,
      attributes: songLineAttributes(song),
    };

    const addOnLines = song.addOns.map((key) => {
      const addOn = addOns.find((candidate) => candidate.key === key);
      const variant = selectedVariant(addOn ? getProductByHandle(catalog, addOn.handle) : undefined);
      if (!addOn || !variant) {
        throw new Error("One selected add-on is not available right now.");
      }

      return {
        variant,
        attributes: addOnLineAttributes(song, addOn),
      };
    });

    return [signatureLine, ...addOnLines];
  });
}

interface StorefrontApiProduct {
  handle: string;
  title: string;
  description: string;
  availableForSale: boolean;
  variants: {
    nodes: Array<{
      id: string;
      title: string;
      availableForSale: boolean;
      price: {
        amount: string;
        currencyCode: string;
      };
    }>;
  };
}
